import { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Phone, 
  MessageSquare, 
  Video, 
  Save, 
  DollarSign, 
  Link as LinkIcon, 
  Type,
  Plus,
  Trash2,
  Edit,
  X,
  GripVertical,
  Clock,
  CheckCircle,
  Sparkles,
  Package,
  Users,
  Star,
  Zap,
  Heart,
  Target,
  BookOpen,
  Headphones,
  Monitor,
  Smartphone,
  Globe
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Available icons for sessions
const iconOptions = [
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'message', label: 'Message/Chat', icon: MessageSquare },
  { value: 'headphones', label: 'Headphones', icon: Headphones },
  { value: 'monitor', label: 'Screen Share', icon: Monitor },
  { value: 'smartphone', label: 'Mobile', icon: Smartphone },
  { value: 'globe', label: 'Online', icon: Globe },
  { value: 'users', label: 'Group', icon: Users },
  { value: 'star', label: 'Premium', icon: Star },
  { value: 'zap', label: 'Quick/Express', icon: Zap },
  { value: 'heart', label: 'Wellness', icon: Heart },
  { value: 'target', label: 'Goal Setting', icon: Target },
  { value: 'book', label: 'Consultation', icon: BookOpen },
  { value: 'sparkles', label: 'Special', icon: Sparkles },
];

const getIconComponent = (iconValue) => {
  const found = iconOptions.find(opt => opt.value === iconValue);
  return found?.icon || Phone;
};

// Default session template
const defaultSession = {
  id: '',
  title: '',
  description: '',
  icon: 'phone',
  price: '$99',
  duration: '60 min',
  features: ['Personal one-on-one session', 'Customized guidance'],
  button_text: 'Purchase Now',
  purchase_link: '',
  is_active: true,
  sort_order: 0
};

export default function AdminPurchaseSettings() {
  const { currentAdminTheme } = useTheme();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Form state for add/edit
  const [formData, setFormData] = useState({ ...defaultSession });
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/admin/coaching-sessions`, { withCredentials: true });
      if (response.data && response.data.length > 0) {
        setSessions(response.data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      } else {
        // Initialize with default sessions if none exist
        setSessions([
          {
            id: 'phone',
            title: 'Phone Session',
            description: 'One-on-one phone coaching session',
            icon: 'phone',
            price: '$99',
            duration: '60 min',
            features: ['Personal guidance', 'Flexible scheduling', 'Recording available'],
            button_text: 'Purchase Now',
            purchase_link: '',
            is_active: true,
            sort_order: 0
          },
          {
            id: 'chat',
            title: 'Chat Session',
            description: 'Text-based coaching conversation',
            icon: 'message',
            price: '$79',
            duration: '45 min',
            features: ['Written responses', 'Transcript provided', 'Follow-up included'],
            button_text: 'Purchase Now',
            purchase_link: '',
            is_active: true,
            sort_order: 1
          },
          {
            id: 'video',
            title: 'Video Session',
            description: 'Face-to-face video coaching',
            icon: 'video',
            price: '$149',
            duration: '60 min',
            features: ['Screen sharing', 'Visual exercises', 'Recording available'],
            button_text: 'Purchase Now',
            purchase_link: '',
            is_active: true,
            sort_order: 2
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      // Initialize with defaults on error
      setSessions([
        { id: 'phone', title: 'Phone Session', description: 'One-on-one phone coaching', icon: 'phone', price: '$99', duration: '60 min', features: [], button_text: 'Purchase Now', purchase_link: '', is_active: true, sort_order: 0 },
        { id: 'chat', title: 'Chat Session', description: 'Text-based coaching', icon: 'message', price: '$79', duration: '45 min', features: [], button_text: 'Purchase Now', purchase_link: '', is_active: true, sort_order: 1 },
        { id: 'video', title: 'Video Session', description: 'Face-to-face video coaching', icon: 'video', price: '$149', duration: '60 min', features: [], button_text: 'Purchase Now', purchase_link: '', is_active: true, sort_order: 2 }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/coaching-sessions`, { sessions }, { withCredentials: true });
      toast.success('All coaching sessions saved successfully!');
    } catch (error) {
      toast.error('Failed to save sessions');
    } finally {
      setSaving(false);
    }
  };

  const handleAddSession = () => {
    const newId = `session_${Date.now()}`;
    setFormData({ 
      ...defaultSession, 
      id: newId,
      sort_order: sessions.length 
    });
    setShowAddDialog(true);
  };

  const handleEditSession = (session) => {
    setSelectedSession(session);
    setFormData({ ...session });
    setShowEditDialog(true);
  };

  const handleDeleteSession = (session) => {
    setSelectedSession(session);
    setShowDeleteDialog(true);
  };

  const confirmAddSession = () => {
    if (!formData.title) {
      toast.error('Session title is required');
      return;
    }
    setSessions([...sessions, { ...formData }]);
    setShowAddDialog(false);
    setFormData({ ...defaultSession });
    toast.success('Session added! Remember to save changes.');
  };

  const confirmEditSession = () => {
    if (!formData.title) {
      toast.error('Session title is required');
      return;
    }
    setSessions(sessions.map(s => s.id === formData.id ? { ...formData } : s));
    setShowEditDialog(false);
    setSelectedSession(null);
    toast.success('Session updated! Remember to save changes.');
  };

  const confirmDeleteSession = () => {
    setSessions(sessions.filter(s => s.id !== selectedSession.id));
    setShowDeleteDialog(false);
    setSelectedSession(null);
    toast.success('Session deleted! Remember to save changes.');
  };

  const handleToggleActive = (sessionId) => {
    setSessions(sessions.map(s => 
      s.id === sessionId ? { ...s, is_active: !s.is_active } : s
    ));
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...(formData.features || []), newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const moveSession = (index, direction) => {
    const newSessions = [...sessions];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= sessions.length) return;
    
    [newSessions[index], newSessions[targetIndex]] = [newSessions[targetIndex], newSessions[index]];
    
    // Update sort orders
    newSessions.forEach((s, i) => s.sort_order = i);
    setSessions(newSessions);
  };

  const renderSessionForm = () => {
    const IconComponent = getIconComponent(formData.icon);
    
    return (
      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
        {/* Basic Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Session Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Phone Session"
              data-testid="session-title-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Icon</Label>
            <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
              <SelectTrigger data-testid="session-icon-select">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <IconComponent className="w-4 h-4" />
                    {iconOptions.find(o => o.value === formData.icon)?.label}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {iconOptions.map(opt => {
                  const Icon = opt.icon;
                  return (
                    <SelectItem key={opt.value} value={opt.value}>
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" />
                        {opt.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description of this session type"
            rows={2}
            data-testid="session-description-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 opacity-60" />
              Price
            </Label>
            <Input
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              placeholder="e.g., $99"
              data-testid="session-price-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Clock className="w-4 h-4 opacity-60" />
              Duration
            </Label>
            <Input
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
              placeholder="e.g., 60 min"
              data-testid="session-duration-input"
            />
          </div>
        </div>

        {/* Features */}
        <div className="space-y-2">
          <Label>Features / Benefits</Label>
          <div className="flex gap-2">
            <Input
              value={newFeature}
              onChange={(e) => setNewFeature(e.target.value)}
              placeholder="Add a feature..."
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
            />
            <Button type="button" onClick={addFeature} variant="outline">
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {(formData.features || []).map((feature, index) => (
              <span 
                key={index}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm"
                style={{ backgroundColor: `${currentAdminTheme.primary}15`, color: currentAdminTheme.primary }}
              >
                <CheckCircle className="w-3 h-3" />
                {feature}
                <button 
                  type="button"
                  onClick={() => removeFeature(index)}
                  className="ml-1 hover:opacity-70"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Purchase Settings */}
        <div 
          className="p-4 rounded-xl space-y-4"
          style={{ backgroundColor: `${currentAdminTheme.muted}50` }}
        >
          <h4 className="font-medium flex items-center gap-2">
            <Package className="w-4 h-4" />
            Purchase Settings
          </h4>
          
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Type className="w-4 h-4 opacity-60" />
              Button Text
            </Label>
            <Input
              value={formData.button_text}
              onChange={(e) => setFormData({ ...formData, button_text: e.target.value })}
              placeholder="e.g., Purchase Now"
            />
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4 opacity-60" />
              Purchase Link (optional)
            </Label>
            <Input
              value={formData.purchase_link}
              onChange={(e) => setFormData({ ...formData, purchase_link: e.target.value })}
              placeholder="https://payment-processor.com/..."
            />
            <p className="text-xs opacity-60">Leave empty to show contact form instead</p>
          </div>
        </div>

        {/* Active Toggle */}
        <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: currentAdminTheme.muted }}>
          <div>
            <p className="font-medium">Active Status</p>
            <p className="text-sm opacity-60">Show this session on the coaching page</p>
          </div>
          <Switch
            checked={formData.is_active}
            onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
          />
        </div>
      </div>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: currentAdminTheme.foreground }}>
              Coaching Sessions
            </h1>
            <p className="opacity-70 mt-1">
              Create, edit, and manage coaching session types for your clients
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleAddSession}
              variant="outline"
              className="gap-2"
              data-testid="add-session-btn"
            >
              <Plus className="w-4 h-4" />
              Add Session
            </Button>
            <Button
              onClick={handleSaveAll}
              disabled={saving}
              className="gap-2"
              style={{ backgroundColor: currentAdminTheme.primary }}
              data-testid="save-all-sessions-btn"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </Button>
          </div>
        </div>

        {/* Sessions List */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-64 rounded-xl animate-pulse" style={{ backgroundColor: currentAdminTheme.border }} />
            ))}
          </div>
        ) : sessions.length === 0 ? (
          <div 
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: currentAdminTheme.cardBackground, border: `1px solid ${currentAdminTheme.border}` }}
          >
            <Package className="w-12 h-12 mx-auto opacity-30 mb-4" />
            <h3 className="text-lg font-medium mb-2" style={{ color: currentAdminTheme.foreground }}>
              No Coaching Sessions
            </h3>
            <p className="opacity-60 mb-4">Create your first coaching session type</p>
            <Button onClick={handleAddSession} className="gap-2" style={{ backgroundColor: currentAdminTheme.primary }}>
              <Plus className="w-4 h-4" />
              Add Session
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map((session, index) => {
              const IconComponent = getIconComponent(session.icon);
              return (
                <div
                  key={session.id}
                  className={`rounded-xl p-6 transition-opacity ${!session.is_active ? 'opacity-50' : ''}`}
                  style={{ 
                    backgroundColor: currentAdminTheme.cardBackground,
                    border: `1px solid ${currentAdminTheme.border}`
                  }}
                  data-testid={`session-card-${session.id}`}
                >
                  <div className="flex items-start gap-4">
                    {/* Drag Handle & Reorder */}
                    <div className="flex flex-col gap-1">
                      <button 
                        onClick={() => moveSession(index, 'up')}
                        disabled={index === 0}
                        className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                      >
                        <GripVertical className="w-4 h-4 rotate-180" />
                      </button>
                      <button 
                        onClick={() => moveSession(index, 'down')}
                        disabled={index === sessions.length - 1}
                        className="p-1 rounded hover:bg-black/5 disabled:opacity-30"
                      >
                        <GripVertical className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Icon */}
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                    >
                      <IconComponent className="w-7 h-7" style={{ color: currentAdminTheme.primary }} />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg" style={{ color: currentAdminTheme.foreground }}>
                          {session.title}
                        </h3>
                        {!session.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                            Inactive
                          </span>
                        )}
                      </div>
                      <p className="text-sm opacity-60 mb-3">{session.description}</p>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 font-semibold" style={{ color: currentAdminTheme.primary }}>
                          <DollarSign className="w-4 h-4" />
                          {session.price}
                        </span>
                        <span className="flex items-center gap-1 opacity-60">
                          <Clock className="w-4 h-4" />
                          {session.duration}
                        </span>
                        <span className="flex items-center gap-1 opacity-60">
                          <CheckCircle className="w-4 h-4" />
                          {session.features?.length || 0} features
                        </span>
                      </div>

                      {/* Features Preview */}
                      {session.features && session.features.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {session.features.slice(0, 3).map((feature, i) => (
                            <span 
                              key={i}
                              className="text-xs px-2 py-1 rounded-full"
                              style={{ backgroundColor: `${currentAdminTheme.secondary}15`, color: currentAdminTheme.secondary }}
                            >
                              {feature}
                            </span>
                          ))}
                          {session.features.length > 3 && (
                            <span className="text-xs px-2 py-1 rounded-full opacity-60">
                              +{session.features.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={session.is_active}
                        onCheckedChange={() => handleToggleActive(session.id)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditSession(session)}
                        data-testid={`edit-session-${session.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteSession(session)}
                        className="text-red-500 hover:text-red-600 hover:border-red-300"
                        data-testid={`delete-session-${session.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Preview Section */}
        {sessions.length > 0 && (
          <div 
            className="rounded-xl p-6"
            style={{ 
              backgroundColor: currentAdminTheme.cardBackground,
              border: `1px solid ${currentAdminTheme.border}`
            }}
          >
            <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: currentAdminTheme.foreground }}>
              <Sparkles className="w-5 h-5" style={{ color: currentAdminTheme.secondary }} />
              Preview (as seen on Coaching Page)
            </h3>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {sessions.filter(s => s.is_active).map((session) => {
                const IconComponent = getIconComponent(session.icon);
                return (
                  <div 
                    key={session.id} 
                    className="p-5 rounded-xl"
                    style={{ backgroundColor: currentAdminTheme.background, border: `1px solid ${currentAdminTheme.border}` }}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{ backgroundColor: `${currentAdminTheme.primary}20` }}
                      >
                        <IconComponent className="w-5 h-5" style={{ color: currentAdminTheme.primary }} />
                      </div>
                      <div>
                        <h4 className="font-medium" style={{ color: currentAdminTheme.foreground }}>{session.title}</h4>
                        <p className="text-xs opacity-60">{session.duration}</p>
                      </div>
                    </div>
                    <p className="text-sm opacity-70 mb-3">{session.description}</p>
                    <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: currentAdminTheme.border }}>
                      <span className="font-bold text-lg" style={{ color: currentAdminTheme.primary }}>
                        {session.price}
                      </span>
                      <Button
                        size="sm"
                        style={{ backgroundColor: currentAdminTheme.secondary }}
                      >
                        {session.button_text || 'Purchase'}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Add Session Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add New Coaching Session
            </DialogTitle>
          </DialogHeader>
          {renderSessionForm()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmAddSession}
              style={{ backgroundColor: currentAdminTheme.primary }}
              data-testid="confirm-add-session"
            >
              Add Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Session Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Coaching Session
            </DialogTitle>
          </DialogHeader>
          {renderSessionForm()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmEditSession}
              style={{ backgroundColor: currentAdminTheme.primary }}
              data-testid="confirm-edit-session"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedSession?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteSession}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-session"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
